import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAlertContext } from '../context/AlertContext';
import { getUserSkills, updateUserSkills } from '../services/userService';
import { analyzeCareer } from '../services/analysisService';
import html2pdf from 'html2pdf.js';
// import { API_URL } from '../config/api';
import { FaPlus, FaTrash, FaChartLine, FaRoad, FaClock, FaLightbulb, FaGraduationCap, FaRocket, FaDownload, FaBriefcase, FaTimes } from 'react-icons/fa';
import { TbTargetArrow, TbBrain, TbTrendingUp } from 'react-icons/tb';
import { HiAcademicCap, HiSparkles } from 'react-icons/hi';

const SkillsAnalysis = () => {
  const { user, setUser } = useAuth();
  const { showAlert } = useAlertContext();
  const [skills, setSkills] = useState([{ name: '', proficiency: 'Beginner' }]);
  const [language, setLanguage] = useState('English');
  const [duplicateError, setDuplicateError] = useState('');

  // Prefill existing skills with deduplication
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const data = await getUserSkills();
        
        if (data.skills && Array.isArray(data.skills) && data.skills.length) {
          // Deduplicate skills by name (case-insensitive)
          const uniqueSkillsMap = new Map();
          data.skills.forEach(skill => {
            if (skill?.name) {
              const key = skill.name.trim().toLowerCase();
              if (!uniqueSkillsMap.has(key)) {
                uniqueSkillsMap.set(key, skill);
              }
            }
          });
          
          const uniqueSkills = Array.from(uniqueSkillsMap.values());
          
          // Only update state if we have skills to display
          if (uniqueSkills.length > 0) {
            setSkills(uniqueSkills);
          } else {
            // If no skills, ensure we have at least one empty field
            setSkills([{ name: '', proficiency: 'Beginner' }]);
          }
        }
      } catch (err) {
        console.error('Error fetching saved skills', err);
        // Only show alert if it's not a 404 (user not found) or network error that might be temporary
        // and definitely don't show "Input Required" for a fetch error
        if (err.response?.status !== 404) {
             showAlert({
              type: 'error',
              title: 'Error Loading Skills',
              message: 'Could not load your saved skills. Please try again.'
            });
        }
        setSkills([{ name: '', proficiency: 'Beginner' }]);
      }
    };
    
    fetchSkills();
  }, []);
  
  const [targetRole, setTargetRole] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
 
  const loadingMessages = [
    'Evaluating your skills...',
    'Identifying gaps...',
    'Mapping to career roles...',
    'Fetching growth resources...',
    'Almost done...'
  ];
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [error, setError] = useState('');
  const reportRef = useRef(null);

  const downloadPDF = () => {
    if (!reportRef.current) return;
  
    const element = reportRef.current;
  
    const opt = {
      margin: [10, 15, 15, 15],
      filename: `${targetRole ? targetRole.toLowerCase().replace(/\s+/g, '-') : 'skill-gap'}-analysis-report.pdf`,
      image: { type: 'jpeg', quality: 1, useCORS: true },
      html2canvas: { scale: 3, useCORS: true, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    };
  
    // Calculate scale so content fits A4 width
    const a4Width = 210; // mm
    const elementWidthPx = element.scrollWidth;
    const pxPerMm = 96 / 25.4; // 96dpi assumption
    const elementWidthMm = elementWidthPx / pxPerMm;
  
    if (elementWidthMm > a4Width) {
      const scaleFactor = a4Width / elementWidthMm;
      opt.html2canvas.scale = 3 * scaleFactor; // adjust dynamically
    }
  
    html2pdf().set(opt).from(element).save();
  };

  const handleAddSkill = () => {
    // Check if the last skill is empty before adding a new one
    if (skills.length > 0 && !skills[skills.length - 1].name.trim()) {
      setDuplicateError('Please fill in the current skill before adding a new one');
      return;
    }
    
    // Check if the last skill is a duplicate
    if (skills.length > 0) {
      const lastSkill = skills[skills.length - 1];
      const isDuplicate = skills.slice(0, -1).some(skill => 
        skill.name.trim().toLowerCase() === lastSkill.name.trim().toLowerCase()
      );
      
      if (isDuplicate) {
        setDuplicateError('This skill has already been added');
        return;
      }
    }
    
    setSkills([...skills, { name: '', proficiency: 'Beginner' }]);
    setDuplicateError('');
  };

  const handleSkillChange = (index, field, value) => {
    const newSkills = [...skills];
    
    // If changing the skill name, check for duplicates
    if (field === 'name') {
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.toLowerCase();
      
      // Check for duplicates in other fields
      const isDuplicate = newSkills.some((skill, i) => 
        i !== index && 
        skill.name.trim().toLowerCase() === normalizedValue &&
        normalizedValue !== ''
      );
      
      if (isDuplicate) {
        setDuplicateError('This skill has already been added');
        return;
      } else {
        setDuplicateError('');
      }
      
      // Update the value
      newSkills[index][field] = value;
    } else {
      newSkills[index][field] = value;
    }
    
    setSkills(newSkills);
  };

  const handleRemoveSkill = (index) => {
    const newSkills = skills.filter((_, i) => i !== index);
    setSkills(newSkills);
  };

  // Rotate loader messages
  useEffect(() => {
    let intervalId;
    if (loading) {
      intervalId = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 2000);
    } else {
      setLoadingMessageIndex(0);
    }
    return () => clearInterval(intervalId);
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setAnalysis(null);

    try {
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Add language to the request data
      const requestData = {
        skills: skills.filter(skill => skill.name.trim() !== ''),
        targetRole,
        language
      };

      // Configure axios headers
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Fire both requests in parallel to cut total waiting time
      const skillsPromise = updateUserSkills(skills.filter(skill => skill.name.trim() !== ''));

      const analysisPromise = analyzeCareer({
        skills,
        targetRole,
        language
      });

      // Wait primarily for analysis – this is what the user cares about
      const analysisResponse = await analysisPromise;
      setAnalysis(analysisResponse);

      // Verify if frontend state needs to be synced with server state
      if (analysisResponse.userStats) {
        const updatedStats = analysisResponse.userStats;
        setUser(prev => {
            const newUser = {
                ...prev,
                skillAnalysisCount: updatedStats.skillAnalysisCount,
                roadmapGenCount: updatedStats.roadmapGenCount,
                planType: updatedStats.planType
            };
            // Update storage as well to persist refresh
            localStorage.setItem('user', JSON.stringify(newUser));
            return newUser;
        });
      }

      // Handle skill-persistence result without blocking UI
      skillsPromise
        .then(res => {
          if (!res.success) {
            console.warn('Skill update failed:', res.message);
          }
        })
        .catch(err => {
          console.warn('Error updating skills:', err);
        });
    } catch (err) {
      console.error('Error details:', err);
      // Detect usage limit error for free users (HTTP 403)

      setError(
        err.response?.data?.message || 
        err.message || 
        'An error occurred while updating skills or analyzing career path'
      );
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{
      backgroundImage: `
        radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
        radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
      `
    }}>
      {/* Top Decoration */}
      <div className="h-[40rem] bg-gradient-to-r from-primary-600 to-indigo-700 w-full absolute top-0 z-0 rounded-b-[3rem] shadow-lg">
         <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      </div>

      <div className="container-responsive relative z-10 pt-20 px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
             <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md border border-white/30 text-white rounded-full text-sm font-medium mb-6 shadow-lg">
                <TbTargetArrow className="w-4 h-4 mr-2" />
                Skills Analysis
             </div>
             
             

            <h1 className="heading-primary mb-6 text-white drop-shadow-sm">
              Analyze Your <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">Career Path</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              Get personalized insights into your skills gap and a roadmap to your dream career.
            </p>
          </div>

          <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/50 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            
            <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
              {/* Skills Section */}
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-primary-500/30">
                    <TbBrain className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="heading-tertiary text-gray-900">Enter Your Skills</h2>
                    <p className="text-gray-500 font-medium">Add your current skills and proficiency levels</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {skills.map((skill, index) => (
                    <div
                      key={index}
                      className="flex flex-col md:flex-row gap-4 p-4 bg-white/50 rounded-2xl border border-gray-100 hover:border-primary-200 transition-colors shadow-sm"
                    >
                      <div className="flex-1">
                        <input
                          type="text"
                          value={skill.name}
                          onChange={(e) => handleSkillChange(index, 'name', e.target.value)}
                          placeholder="e.g. JavaScript, Python, Design"
                          className={`w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-50 focus:border-primary-500 transition-all font-medium text-gray-900 placeholder-gray-400 ${duplicateError ? 'border-red-500 focus:ring-red-100' : ''}`}
                          required
                          onFocus={() => setDuplicateError('')}
                        />
                        {duplicateError && (
                          <p className="mt-2 text-sm text-red-500 font-medium ml-1">{duplicateError}</p>
                        )}
                      </div>
                      <div className="w-full md:w-48">
                         <div className="relative">
                            <select
                              value={skill.proficiency}
                              onChange={(e) => handleSkillChange(index, 'proficiency', e.target.value)}
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-50 focus:border-primary-500 transition-all font-medium text-gray-700 appearance-none cursor-pointer"
                            >
                              <option value="Beginner">Beginner</option>
                              <option value="Intermediate">Intermediate</option>
                              <option value="Advanced">Advanced</option>
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(index)}
                        className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all self-end md:self-auto"
                        disabled={skills.length === 1}
                        title="Remove skill"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={handleAddSkill}
                  className="mt-6 px-6 py-2.5 bg-gray-50 hover:bg-white text-gray-700 font-bold rounded-xl border border-gray-200 hover:border-primary-200 hover:text-primary-600 transition-all shadow-sm flex items-center group"
                >
                  <FaPlus className="w-3 h-3 mr-2 group-hover:rotate-90 transition-transform duration-300" />
                  Add Another Skill
                </button>
              </div>

              {/* Target Role Selection */}
              <div className="border-t border-gray-100 pt-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-pink-500 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-secondary-500/30">
                    <FaRocket className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="heading-tertiary text-gray-900">Target Role</h2>
                    <p className="text-gray-500 font-medium">What position are you aiming for?</p>
                  </div>
                </div>
                
                <div className="bg-white/50 p-1 rounded-2xl">
                  <input
                    type="text"
                    value={targetRole}
                    onChange={(e) => setTargetRole(e.target.value)}
                    placeholder="e.g. Senior Frontend Developer"
                    className="w-full px-6 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-secondary-50 focus:border-secondary-500 transition-all text-lg font-bold text-gray-900 placeholder-gray-400"
                    required
                  />
                </div>
                 <p className="text-sm text-gray-500 mt-3 ml-2 flex items-center">
                    <FaLightbulb className="text-yellow-500 mr-2" />
                    Be specific for better results (e.g. "React Native Developer" instead of "Developer")
                  </p>
              </div>
             

              {/* Submit Button */}
              <div className="border-t border-gray-100 pt-8">
                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl font-bold text-lg shadow-xl shadow-primary-500/30 hover:shadow-2xl hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-not-allowed group relative overflow-hidden"
                  disabled={loading}
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  <div className="relative flex items-center justify-center">
                      {loading ? (
                        <>
                           <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <TbTrendingUp className="w-6 h-6 mr-2 group-hover:animate-bounce" />
                          Generate Analysis
                        </>
                      )}
                  </div>
                </button>
              </div>
            </form>

            {/* Error Message */}
            {error && (
              <div className="mt-8 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center animate-shake">
                <div className="w-8 h-8 bg-red-100 text-red-500 rounded-lg flex items-center justify-center mr-3">
                    <FaTimes />
                </div>
                <p className="text-red-700 font-bold">{error}</p>
              </div>
            )}
          </div>

          {/* Analysis Results */}
          {analysis && (
            <div ref={reportRef} className="mt-12 space-y-8 animate-fade-in-up">
              
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
                 <div>
                    <h2 className="heading-secondary text-gray-900">Analysis Results</h2>
                    <p className="text-gray-600 font-medium">For <span className="text-primary-600">{targetRole}</span></p>
                 </div>
                 <button onClick={downloadPDF} className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 hover:text-primary-600 transition-colors shadow-sm flex items-center">
                    <FaDownload className="mr-2" /> Download Report
                 </button>
              </div>
              
              {/* Skills Gap Analysis */}
              <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/50">
                <div className="flex items-center mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-orange-500/30">
                    <FaChartLine className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Skills Gap Analysis</h3>
                    <p className="text-gray-500 font-medium">Areas to improve for your target role</p>
                  </div>
                </div>
                
                <div className="grid gap-6">
                  {analysis.skillsGap.map((gap, index) => (
                    <div key={index} className="group p-6 bg-gradient-to-r from-orange-50 to-white rounded-2xl border border-orange-100 hover:border-orange-200 transition-all shadow-sm hover:shadow-md">
                      <div className="flex items-start">
                          <div className="mt-1 mr-4">
                              <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-orange-600 transition-colors">{gap.skill}</h4>
                            <p className="text-gray-700 leading-relaxed text-sm">{gap.description}</p>
                          </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

             
                 {/* Related Jobs */}
                 {analysis.relatedJobs && analysis.relatedJobs.length > 0 && (
                <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/50">
                  <div className="flex items-center mb-8">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-purple-500/30">
                      <FaBriefcase className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Related Opportunities</h3>
                      <p className="text-gray-500 font-medium">Jobs matching your current profile</p>
                    </div>
                  </div>
                  
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {analysis.relatedJobs.map((job, index) => (
                      <a 
                        key={index} 
                        href={job.applyLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block group"
                      >
                        <div className="h-full bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                          <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                                  {job.title}
                                </h4>
                                <p className="text-indigo-600 font-bold text-sm">{job.company}</p>
                              </div>
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700">
                                {job.location}
                              </span>
                            </div>
                            
                            <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">{job.description}</p>
                            
                            <div className="mb-4">
                              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">REQUIRED SKILLS</div>
                              <div className="flex flex-wrap gap-2">
                                {job.requiredSkills && job.requiredSkills.slice(0, 3).map((skill, i) => (
                                  <span key={i} className="px-2.5 py-1 text-xs font-medium rounded-lg bg-gray-50 text-gray-600 border border-gray-100">
                                    {skill}
                                  </span>
                                ))}
                                {job.requiredSkills && job.requiredSkills.length > 3 && (
                                  <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-gray-50 text-gray-400 border border-gray-100">
                                    +{job.requiredSkills.length - 3}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {job.salary && (
                              <div className="flex items-center text-sm font-bold text-gray-900 mb-4 bg-green-50 w-fit px-3 py-1 rounded-lg text-green-700">
                                <span className="mr-1">💰</span> {job.salary}
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
                              <span className="text-indigo-600 text-sm font-bold group-hover:underline">
                                View Details
                              </span>
                              <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center max-w-sm w-full mx-4">
              <div className="relative w-20 h-20 mb-6">
                   <div className="absolute inset-0 border-4 border-gray-100 rounded-full"></div>
                   <div className="absolute inset-0 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
                   <div className="absolute inset-0 flex items-center justify-center">
                       <TbBrain className="w-8 h-8 text-primary-600 animate-pulse" />
                   </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Analyzing...</h3>
              <p className="text-gray-500 text-center font-medium animate-pulse">
                {loadingMessages[loadingMessageIndex]}
              </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsAnalysis;